/**
 * EmailTemplateWizard — 5 adımlı sihirbaz.
 *
 * Adımlar:
 *   1) Temel bilgiler  — Ad, açıklama, kategori, gönderen
 *   2) Tetikleyici     — Ne tetikliyor + sıklık
 *   3) Alıcılar        — Kim mail alacak (çoklu seçim + config)
 *   4) Kurallar        — Gönderim kuralları (çoklu seçim + custom)
 *   5) İçerik & Test   — Konu, gövde ({{variables}}), test gönderim, kaydet
 *
 * Backend endpoints:
 *   GET  /api/emails/catalog                          (dropdown metadata)
 *   POST /api/emails/custom                           (kaydet)
 *   PATCH /api/emails/custom/{id}                     (düzenle)
 *   POST /api/emails/custom/draft-test                (kaydetmeden test)
 *   POST /api/emails/custom/{id}/test                 (kayıtlı test)
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Send,
  Plus,
  X,
  Sparkles,
  Bold,
  Italic,
  Link as LinkIcon,
  List as ListIcon,
  Braces,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const STEPS = [
  { key: "basics", title: "Temel bilgiler", desc: "Ad, kategori ve gönderen" },
  { key: "trigger", title: "Tetikleyici", desc: "Ne tetikleyecek + sıklık" },
  { key: "recipients", title: "Alıcılar", desc: "Kim mail alacak" },
  { key: "rules", title: "Kurallar", desc: "Gönderim koşulları" },
  { key: "content", title: "İçerik & Test", desc: "Konu, mesaj, test gönder" },
];

const CATEGORIES = [
  { id: "custom", label: "Özel" },
  { id: "user_lifecycle", label: "Kullanıcı" },
  { id: "task", label: "Görev" },
  { id: "notification", label: "Bildirim" },
  { id: "digest", label: "Özet" },
  { id: "billing", label: "Fatura" },
];

export default function EmailTemplateWizard({
  open,
  onClose,
  onSaved,
  editing, // if provided, wizard runs in edit mode
  defaultTestRecipient = "mbucak@gmail.com",
}) {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);

  const [form, setForm] = useState(() => initialForm());
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testRecipient, setTestRecipient] = useState(defaultTestRecipient);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setTestResult(null);
    setTestRecipient(defaultTestRecipient);
    if (editing) {
      setForm(hydrateFromEditing(editing));
    } else {
      setForm(initialForm());
    }
    (async () => {
      try {
        const { data } = await api.get("/emails/catalog");
        setCatalog(data);
      } catch (err) {
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "Katalog yüklenemedi",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [open, editing, defaultTestRecipient]);

  const triggerGroups = useMemo(() => {
    if (!catalog?.triggers) return {};
    return catalog.triggers.reduce((acc, t) => {
      (acc[t.group || "Diğer"] = acc[t.group || "Diğer"] || []).push(t);
      return acc;
    }, {});
  }, [catalog]);

  // ---------- Step validation ----------
  const stepValid = (idx) => {
    if (idx === 0) return form.name.trim().length > 0;
    if (idx === 1) return !!form.trigger_id && !!form.frequency;
    if (idx === 2) return form.recipient_ids.length > 0;
    if (idx === 3) return true; // rules optional
    if (idx === 4)
      return form.subject.trim().length > 0 && form.body.trim().length > 0;
    return true;
  };

  const goNext = () => {
    if (!stepValid(step)) {
      toast.error("Bu adımdaki zorunlu alanları doldur");
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const save = async () => {
    if (!stepValid(4)) {
      toast.error("Konu ve mesaj zorunlu");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        category: form.category,
        sender: form.sender,
        trigger_id: form.trigger_id,
        frequency: form.frequency,
        recipient_ids: form.recipient_ids,
        recipient_config: form.recipient_config,
        rule_ids: form.rule_ids,
        custom_rules: form.custom_rules.filter((r) => r.trim()),
        subject: form.subject.trim(),
        body: form.body.trim(),
        cta_url: form.cta_url || null,
        cta_label: form.cta_label || null,
        active: form.active,
      };
      if (editing?.id && !editing._cloneFromBuiltin) {
        await api.patch(`/emails/custom/${editing.id}`, payload);
        toast.success("Şablon güncellendi");
      } else {
        await api.post("/emails/custom", payload);
        toast.success(
          editing?._cloneFromBuiltin
            ? "Şablon özel kopyanla kaydedildi"
            : "Şablon kaydedildi",
        );
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Kaydedilemedi",
      );
    } finally {
      setSaving(false);
    }
  };

  const testDraft = async () => {
    if (!form.subject.trim() || !form.body.trim()) {
      toast.error("Test için konu ve mesaj gerekli");
      return;
    }
    if (!testRecipient.includes("@")) {
      toast.error("Geçerli bir e-posta gir");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/emails/custom/draft-test", {
        to_email: testRecipient,
        subject: form.subject.trim(),
        body: form.body.trim(),
        name: form.name || "Draft",
        cta_url: form.cta_url || null,
        cta_label: form.cta_label || null,
      });
      setTestResult(data);
      if (data?.delivery?.delivered) {
        toast.success(`Test e-postası ${testRecipient} adresine gönderildi`);
      } else if (data?.delivery?.provider === "outbox") {
        toast.info("SendGrid yok — outbox'a düştü");
      } else {
        toast.error(data?.delivery?.error || "Test gönderilemedi");
      }
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Test gönderilemedi",
      );
    } finally {
      setTesting(false);
    }
  };

  const insertVariable = (key) => {
    const marker = `{{${key}}}`;
    // Insert at end of body — good enough for MVP.
    setForm((f) => ({ ...f, body: (f.body || "") + marker }));
  };

  const toggleArrayValue = (arrKey, val) => {
    setForm((f) => {
      const set = new Set(f[arrKey] || []);
      if (set.has(val)) set.delete(val);
      else set.add(val);
      return { ...f, [arrKey]: Array.from(set) };
    });
  };

  // ---------- Rendering ----------
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="flex max-h-[90vh] max-w-3xl flex-col overflow-hidden"
        data-testid="email-wizard-dialog"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-600" />
            {editing?._cloneFromBuiltin
              ? "Şablonu özelleştir"
              : editing?.id
                ? "Şablonu düzenle"
                : "Yeni mail şablonu"}
          </DialogTitle>
          {/* Step indicator */}
          <div className="mt-3 flex items-center gap-2" data-testid="email-wizard-steps">
            {STEPS.map((s, i) => (
              <div
                key={s.key}
                className="flex flex-1 items-center gap-2"
                data-testid={`email-wizard-step-${i}`}
              >
                <div
                  className={[
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold",
                    i < step
                      ? "bg-emerald-500 text-white"
                      : i === step
                        ? "bg-violet-600 text-white"
                        : "bg-zinc-200 text-zinc-600",
                  ].join(" ")}
                >
                  {i < step ? <Check className="h-3 w-3" /> : i + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <div
                    className={[
                      "truncate text-[11px] font-semibold",
                      i === step ? "text-zinc-900" : "text-zinc-500",
                    ].join(" ")}
                  >
                    {s.title}
                  </div>
                  <div className="truncate text-[10px] text-zinc-400">
                    {s.desc}
                  </div>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="h-px flex-1 bg-zinc-200" />
                )}
              </div>
            ))}
          </div>
        </DialogHeader>

        {loading || !catalog ? (
          <div className="py-8 text-center">
            <Loader2 className="mx-auto h-5 w-5 animate-spin text-zinc-400" />
            <p className="mt-2 text-xs text-zinc-500">Yükleniyor...</p>
          </div>
        ) : (
          <div
            className="min-h-0 flex-1 overflow-y-auto pr-1"
            data-testid={`email-wizard-panel-${STEPS[step].key}`}
          >
            {step === 0 && <StepBasics form={form} setForm={setForm} catalog={catalog} />}
            {step === 1 && (
              <StepTrigger
                form={form}
                setForm={setForm}
                catalog={catalog}
                triggerGroups={triggerGroups}
              />
            )}
            {step === 2 && (
              <StepRecipients
                form={form}
                setForm={setForm}
                catalog={catalog}
                toggle={(v) => toggleArrayValue("recipient_ids", v)}
              />
            )}
            {step === 3 && (
              <StepRules
                form={form}
                setForm={setForm}
                catalog={catalog}
                toggle={(v) => toggleArrayValue("rule_ids", v)}
              />
            )}
            {step === 4 && (
              <StepContent
                form={form}
                setForm={setForm}
                catalog={catalog}
                testRecipient={testRecipient}
                setTestRecipient={setTestRecipient}
                testing={testing}
                testResult={testResult}
                onTest={testDraft}
                onInsertVariable={insertVariable}
              />
            )}
          </div>
        )}

        <DialogFooter className="flex shrink-0 justify-between gap-2 border-t border-zinc-100 pt-3 sm:justify-between">
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={goBack}
                data-testid="email-wizard-back"
              >
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Geri
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
              data-testid="email-wizard-cancel"
            >
              Vazgeç
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                type="button"
                onClick={goNext}
                disabled={!stepValid(step)}
                data-testid="email-wizard-next"
                className="bg-zinc-900 text-white hover:bg-zinc-800"
              >
                İleri <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={save}
                disabled={saving || !stepValid(4)}
                data-testid="email-wizard-save"
                className="bg-violet-600 text-white hover:bg-violet-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Kaydediliyor
                  </>
                ) : editing?.id && !editing._cloneFromBuiltin ? (
                  <>
                    <Check className="mr-1.5 h-3.5 w-3.5" /> Güncelle
                  </>
                ) : (
                  <>
                    <Check className="mr-1.5 h-3.5 w-3.5" /> Şablonu kaydet
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- helpers ----------
function initialForm() {
  return {
    name: "",
    description: "",
    category: "custom",
    sender: { id: "serpix_default", name: "Serpix", email: "hello@serpix.co" },
    trigger_id: "",
    frequency: "",
    recipient_ids: [],
    recipient_config: {},
    rule_ids: [],
    custom_rules: [],
    subject: "",
    body: "",
    cta_url: "",
    cta_label: "",
    active: true,
  };
}

function hydrateFromEditing(t) {
  // Built-in templates arrive without `trigger_id`/`recipient_ids`/`rule_ids`
  // (they only have display-ready labels + subject/body). When cloning
  // one, best-effort re-populate the id fields so the wizard steps 2-4
  // still show sensible defaults. Users can adjust before saving.
  const isClone = !!t._cloneFromBuiltin;
  return {
    name: isClone ? `${t.name} (özel)` : t.name || "",
    description: t.description || "",
    category: t.category || "custom",
    sender: t.sender || {
      id: "serpix_default",
      name: "Serpix",
      email: "hello@serpix.co",
    },
    trigger_id: t.trigger_id || _guessTriggerFromBuiltinId(t.id) || "",
    frequency: t.frequency || "",
    recipient_ids: t.recipient_ids || [],
    recipient_config: t.recipient_config || {},
    rule_ids: t.rule_ids || [],
    custom_rules: t.custom_rules || (isClone ? t.rules || [] : []),
    subject: t.subject || "",
    // Built-in: only body_preview (variables already substituted).
    // Custom: body_raw is the source with {{variables}}.
    body: t.body_raw || t.body_preview || "",
    cta_url: t.cta_url || "",
    cta_label: t.cta_label || "",
    active: t.active !== false,
    // When cloning built-ins we always POST (never PATCH). The wizard
    // flips this flag by ignoring `editing.id` in the save handler.
    _isClone: isClone,
  };
}

// Map built-in template ids to a sensible trigger id so the cloned
// custom template lights up in the dispatcher automatically.
function _guessTriggerFromBuiltinId(id) {
  return (
    {
      user_invite: "user_invited",
      user_invite_resend: "user_invited",
      tenant_invite: "tenant_invited",
      tenant_invite_resend: "tenant_invited",
      verification: "email_verification_requested",
      password_reset: "password_reset_requested",
      weekly_digest: "weekly_schedule",
      task_escalation: "task_5day_inactive",
      task_assigned: "task_assigned",
      mentioned: "user_mentioned",
      task_commented: "task_commented",
      billing_paid: "billing_paid",
      billing_past_due: "billing_past_due",
    }[id] || null
  );
}

// =============================================================
// Step Components
// =============================================================

function StepBasics({ form, setForm, catalog }) {
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Şablon adı *
        </Label>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Örn: Hoş geldin maili"
          data-testid="email-wizard-name"
          className="mt-1"
        />
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Kısa açıklama
        </Label>
        <Textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Bu şablon ne zaman kullanılıyor?"
          data-testid="email-wizard-description"
          className="mt-1"
          rows={2}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Kategori
          </Label>
          <Select
            value={form.category}
            onValueChange={(v) => setForm({ ...form, category: v })}
          >
            <SelectTrigger data-testid="email-wizard-category" className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-zinc-50/70 p-3">
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Kim Gönderecek?
        </Label>
        <Select
          value={form.sender.id}
          onValueChange={(id) => {
            const s = catalog.senders.find((x) => x.id === id);
            if (!s) return;
            setForm({
              ...form,
              sender: {
                id: s.id,
                name: s.name || form.sender.name,
                email: s.email || form.sender.email,
              },
            });
          }}
        >
          <SelectTrigger data-testid="email-wizard-sender" className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {catalog.senders.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {form.sender.id === "custom" && (
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Input
              placeholder="Gönderen adı"
              value={form.sender.name || ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  sender: { ...form.sender, name: e.target.value },
                })
              }
              data-testid="email-wizard-sender-name"
            />
            <Input
              placeholder="Gönderen e-postası"
              type="email"
              value={form.sender.email || ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  sender: { ...form.sender, email: e.target.value },
                })
              }
              data-testid="email-wizard-sender-email"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function StepTrigger({ form, setForm, catalog, triggerGroups }) {
  const selectedTrigger = catalog.triggers.find(
    (t) => t.id === form.trigger_id,
  );
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Tetikleyen şey ne? *
        </Label>
        <Select
          value={form.trigger_id}
          onValueChange={(id) => {
            const t = catalog.triggers.find((x) => x.id === id);
            setForm({
              ...form,
              trigger_id: id,
              // Auto-fill frequency hint if the user didn't touch it yet.
              frequency: form.frequency || t?.frequency_hint || "",
            });
          }}
        >
          <SelectTrigger data-testid="email-wizard-trigger" className="mt-1">
            <SelectValue placeholder="Tetikleyici seç..." />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            {Object.entries(triggerGroups).map(([group, items]) => (
              <div key={group}>
                <div className="px-2 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  {group}
                </div>
                {items.map((t) => (
                  <SelectItem
                    key={t.id}
                    value={t.id}
                    data-testid={`email-wizard-trigger-opt-${t.id}`}
                  >
                    {t.label}
                  </SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
        {selectedTrigger?.frequency_hint && (
          <p className="mt-1.5 text-[11px] text-zinc-500">
            Önerilen sıklık: <b>{selectedTrigger.frequency_hint}</b>
          </p>
        )}
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Sıklık *
        </Label>
        <Select
          value={form.frequency}
          onValueChange={(v) => setForm({ ...form, frequency: v })}
        >
          <SelectTrigger data-testid="email-wizard-frequency" className="mt-1">
            <SelectValue placeholder="Sıklık seç..." />
          </SelectTrigger>
          <SelectContent>
            {catalog.frequencies.map((f) => (
              <SelectItem
                key={f.id}
                value={f.label}
                data-testid={`email-wizard-frequency-opt-${f.id}`}
              >
                {f.label}
              </SelectItem>
            ))}
            {selectedTrigger?.frequency_hint && (
              <SelectItem value={selectedTrigger.frequency_hint}>
                <span className="italic">
                  {selectedTrigger.frequency_hint}
                </span>
              </SelectItem>
            )}
          </SelectContent>
        </Select>
        <p className="mt-1.5 text-[11px] text-zinc-500">
          Ya listeden seç ya da tetikleyicinin önerdiği sıklığı kullan.
        </p>
      </div>
    </div>
  );
}

function StepRecipients({ form, setForm, catalog, toggle }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">
        Bir veya birden fazla alıcı seçebilirsin. Bazı seçenekler ek yapılandırma
        ister (özel e-posta, belirli kullanıcılar).
      </p>
      <ul className="space-y-1.5">
        {catalog.recipients.map((r) => {
          const checked = form.recipient_ids.includes(r.id);
          return (
            <li key={r.id}>
              <label
                className={[
                  "flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors",
                  checked
                    ? "border-violet-300 bg-violet-50/60"
                    : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50",
                ].join(" ")}
                data-testid={`email-wizard-recipient-${r.id}`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(r.id)}
                  className="mt-0.5"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-zinc-900">
                    {r.label}
                  </div>
                  {r.needs_config && checked && (
                    <div className="mt-2">
                      {r.id === "custom_email" && (
                        <Input
                          type="email"
                          placeholder="ornek@sirket.com"
                          value={form.recipient_config.email || ""}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              recipient_config: {
                                ...f.recipient_config,
                                email: e.target.value,
                              },
                            }))
                          }
                          data-testid="email-wizard-custom-email"
                        />
                      )}
                      {r.id === "specific_users" && (
                        <Input
                          placeholder="user1@acme.com, user2@acme.com"
                          value={
                            (form.recipient_config.emails || []).join(", ") ||
                            ""
                          }
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              recipient_config: {
                                ...f.recipient_config,
                                emails: e.target.value
                                  .split(",")
                                  .map((s) => s.trim())
                                  .filter(Boolean),
                              },
                            }))
                          }
                          data-testid="email-wizard-specific-users"
                        />
                      )}
                    </div>
                  )}
                </div>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StepRules({ form, setForm, catalog, toggle }) {
  const addCustomRule = () => {
    setForm((f) => ({ ...f, custom_rules: [...(f.custom_rules || []), ""] }));
  };
  const updateCustomRule = (idx, v) => {
    setForm((f) => {
      const arr = [...f.custom_rules];
      arr[idx] = v;
      return { ...f, custom_rules: arr };
    });
  };
  const removeCustomRule = (idx) => {
    setForm((f) => {
      const arr = [...f.custom_rules];
      arr.splice(idx, 1);
      return { ...f, custom_rules: arr };
    });
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-zinc-500">
        Bu şablon gönderilirken hangi kurallara uyulacak? İstediğin kadar seç
        veya kendi kuralını yaz.
      </p>
      <ul className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
        {catalog.rules.map((r) => {
          const checked = form.rule_ids.includes(r.id);
          return (
            <li key={r.id}>
              <label
                className={[
                  "flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5 text-xs transition-colors",
                  checked
                    ? "border-amber-300 bg-amber-50/60"
                    : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50",
                ].join(" ")}
                data-testid={`email-wizard-rule-${r.id}`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(r.id)}
                  className="mt-0.5"
                />
                <span className="text-zinc-800">{r.label}</span>
              </label>
            </li>
          );
        })}
      </ul>

      <div className="rounded-lg border border-dashed border-zinc-300 p-3">
        <div className="mb-2 flex items-center justify-between">
          <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Özel kurallar
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCustomRule}
            data-testid="email-wizard-add-custom-rule"
            className="h-7 gap-1 text-[11px]"
          >
            <Plus className="h-3 w-3" /> Kural ekle
          </Button>
        </div>
        {(form.custom_rules || []).length === 0 ? (
          <p className="text-[11px] text-zinc-400">
            İstiyorsan buraya kendi kurallarını yazabilirsin.
          </p>
        ) : (
          <ul className="space-y-2">
            {form.custom_rules.map((rule, i) => (
              <li key={i} className="flex items-center gap-2">
                <Input
                  value={rule}
                  onChange={(e) => updateCustomRule(i, e.target.value)}
                  placeholder="Özel kural..."
                  data-testid={`email-wizard-custom-rule-${i}`}
                  className="h-8 text-xs"
                />
                <button
                  type="button"
                  onClick={() => removeCustomRule(i)}
                  className="rounded-md p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                  data-testid={`email-wizard-custom-rule-remove-${i}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StepContent({
  form,
  setForm,
  catalog,
  testRecipient,
  setTestRecipient,
  testing,
  testResult,
  onTest,
  onInsertVariable,
}) {
  const applyFormatting = (marker) => {
    setForm((f) => ({ ...f, body: (f.body || "") + `${marker}metin${marker}` }));
  };
  const applyList = () => {
    setForm((f) => ({
      ...f,
      body: (f.body || "") + "\n- madde 1\n- madde 2",
    }));
  };
  const applyLink = () => {
    setForm((f) => ({
      ...f,
      body: (f.body || "") + "[link metni](https://example.com)",
    }));
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Konu *
        </Label>
        <Input
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          placeholder="Örn: {{tenant_name}} sizi Serpix'e davet ediyor"
          data-testid="email-wizard-subject"
          className="mt-1"
        />
        <p className="mt-1 text-[10px] text-zinc-400">
          {"{{degisken}}"} kullanabilirsin — aşağıdan hızlıca ekleyebilirsin.
        </p>
      </div>

      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Mesaj *
        </Label>
        {/* Toolbar */}
        <div className="mt-1 flex flex-wrap items-center gap-1 rounded-t-md border border-b-0 border-zinc-200 bg-zinc-50 px-2 py-1">
          <ToolbarBtn
            onClick={() => applyFormatting("**")}
            title="Kalın"
            testId="email-wizard-fmt-bold"
          >
            <Bold className="h-3.5 w-3.5" />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => applyFormatting("*")}
            title="İtalik"
            testId="email-wizard-fmt-italic"
          >
            <Italic className="h-3.5 w-3.5" />
          </ToolbarBtn>
          <ToolbarBtn onClick={applyList} title="Liste" testId="email-wizard-fmt-list">
            <ListIcon className="h-3.5 w-3.5" />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={applyLink}
            title="Bağlantı"
            testId="email-wizard-fmt-link"
          >
            <LinkIcon className="h-3.5 w-3.5" />
          </ToolbarBtn>
          <div className="mx-1 h-4 w-px bg-zinc-300" />
          <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            <Braces className="h-3 w-3" /> Değişkenler:
          </span>
          <div className="flex flex-wrap gap-1">
            {catalog.variables.slice(0, 8).map((v) => (
              <button
                type="button"
                key={v.key}
                onClick={() => onInsertVariable(v.key)}
                data-testid={`email-wizard-var-${v.key}`}
                title={`${v.label} → ${v.sample}`}
                className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-medium text-violet-700 hover:bg-violet-200"
              >
                {"{{"}
                {v.key}
                {"}}"}
              </button>
            ))}
          </div>
        </div>
        <Textarea
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          placeholder="Merhaba {{user_name}},&#10;&#10;{{tenant_name}} ekibi seni davet ediyor..."
          data-testid="email-wizard-body"
          className="rounded-t-none font-mono text-xs"
          rows={9}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            CTA butonu URL (opsiyonel)
          </Label>
          <Input
            value={form.cta_url}
            onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
            placeholder="/app/dashboard"
            data-testid="email-wizard-cta-url"
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            CTA butonu metni (opsiyonel)
          </Label>
          <Input
            value={form.cta_label}
            onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
            placeholder="Hesabımı aç"
            data-testid="email-wizard-cta-label"
            className="mt-1"
          />
        </div>
      </div>

      {/* Test section */}
      <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-4">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-violet-700">
          <Send className="h-3.5 w-3.5" /> Test gönderim
        </div>
        <p className="mb-3 text-[11px] text-violet-800/80">
          Bu şablonu kaydetmeden önce, örnek verilerle gerçek bir e-posta
          göndererek nasıl göründüğünü kontrol edebilirsin.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            type="email"
            value={testRecipient}
            onChange={(e) => setTestRecipient(e.target.value)}
            placeholder="mbucak@gmail.com"
            data-testid="email-wizard-test-recipient"
            className="flex-1"
          />
          <Button
            type="button"
            onClick={onTest}
            disabled={testing}
            data-testid="email-wizard-test-send"
            className="gap-1.5 bg-violet-600 text-white hover:bg-violet-700"
          >
            {testing ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Gönderiliyor
              </>
            ) : (
              <>
                <Send className="h-3.5 w-3.5" /> Test gönder
              </>
            )}
          </Button>
        </div>
        {testResult && (
          <div
            className={[
              "mt-3 rounded-md px-3 py-2 text-xs",
              testResult?.delivery?.delivered
                ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                : "bg-rose-50 text-rose-800 ring-1 ring-rose-200",
            ].join(" ")}
            data-testid="email-wizard-test-result"
          >
            {testResult?.delivery?.delivered ? (
              <>
                <b>✅ Gönderildi.</b> Provider:{" "}
                <code>{testResult.delivery.provider}</code> · Durum:{" "}
                <code>{testResult.delivery.status_code}</code>
              </>
            ) : (
              <>
                <b>❌ Hata:</b>{" "}
                {testResult?.delivery?.error || "Bilinmeyen hata"}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ToolbarBtn({ onClick, title, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testId}
      className="rounded p-1 text-zinc-700 hover:bg-white"
    >
      {children}
    </button>
  );
}
